import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import CensusUploadPanel from "../components/CensusUploadPanel";
import UnderwritingOutcomesPanel from "../components/admin/UnderwritingOutcomesPanel";
import "../components/admin/UnderwritingOutcomesPanel.css";
import {
    adminLogout,
    adminMe,
    adminSubmissionCensusXlsxUrl,
    adminSubmissionDetail,
    adminSubmissionPdfUrl,
    adminTransitionStatus,
    adminListEmployeeApplications,
    adminEmployeeApplicationPdfUrl,
} from "../lib/admin";
import { track } from "../lib/analytics";

import "./Admin.css";

function fmt(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

function riderSummary(riders = {}) {
    const parts = [];
    if (riders.waiver_of_premium) parts.push("Waiver of Premium");
    if (riders.accidental_death_benefit) parts.push("Accidental Death Benefit");
    const dep = riders.dependent_coverage;
    if (dep && dep !== "none" && dep !== false) parts.push("Dependent coverage");
    return parts.join(", ") || "Core only";
}

/* -------------------------------------------------------------------------
 * Phase 2d — Admin batch-invite panel has moved to
 * /app/frontend/src/components/CensusUploadPanel.jsx. It now tabs between
 * an AI-assisted file upload flow (parse → Claude map → validate → commit)
 * and the original CSV-paste fallback.
 * -------------------------------------------------------------------------
 */

/* ---------- Phase 2e — Employee enrollments block ---------- */
function EmployeeEnrollmentsBlock({ applicationId }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await adminListEmployeeApplications(applicationId);
                if (!cancelled) setData(res);
            } catch (e) {
                if (!cancelled) setErr(e?.response?.data?.detail || "Couldn't load enrollments.");
            }
        })();
        return () => { cancelled = true; };
    }, [applicationId]);

    if (err) {
        return (
            <div className="admin-enroll-block" data-testid="admin-enrollments-err">
                <h4>Employee enrollments</h4>
                <div className="review-err">{String(err)}</div>
            </div>
        );
    }
    if (!data) {
        return (
            <div className="admin-enroll-block" data-testid="admin-enrollments-loading">
                <h4>Employee enrollments</h4>
                <div style={{ color: "var(--muted)", fontStyle: "italic" }}>Loading…</div>
            </div>
        );
    }

    const counts = data.counts || {};
    const rows = data.employee_applications || [];
    return (
        <div className="admin-enroll-block" data-testid="admin-enrollments-block">
            <h4>Employee enrollments</h4>
            <div className="admin-enroll-counts">
                <div className="admin-enroll-count">
                    <div className="admin-enroll-count-label">Invited</div>
                    <div className="admin-enroll-count-num">{counts.invited ?? 0}</div>
                </div>
                <div className="admin-enroll-count">
                    <div className="admin-enroll-count-label">Enrolled</div>
                    <div className="admin-enroll-count-num" data-testid="admin-enrolled-count">
                        {counts.enrolled ?? 0}
                    </div>
                </div>
                <div className="admin-enroll-count">
                    <div className="admin-enroll-count-label">In progress</div>
                    <div className="admin-enroll-count-num">{counts.in_progress ?? 0}</div>
                </div>
                <div className="admin-enroll-count">
                    <div className="admin-enroll-count-label">Declined</div>
                    <div className="admin-enroll-count-num">{counts.declined_health ?? 0}</div>
                </div>
            </div>
            <div style={{ color: "var(--muted)", fontSize: "0.88rem", marginBottom: "0.75rem" }}>
                {counts.enrolled ?? 0} of {counts.invited ?? 0} invited employees have enrolled.
            </div>
            {rows.length === 0 ? (
                <div style={{ color: "var(--muted)", fontStyle: "italic", padding: "0.5rem 0" }}>
                    No employee applications yet.
                </div>
            ) : (
                <table className="admin-enroll-table" data-testid="admin-enroll-table">
                    <thead>
                        <tr>
                            <th>Employee</th>
                            <th>Age</th>
                            <th>Coverage</th>
                            <th>Status</th>
                            <th>Submitted</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.application_id} data-testid={`ee-row-${r.application_id}`}>
                                <td>
                                    <strong>{r.first_name} {r.last_name}</strong>
                                    <div style={{ color: "var(--muted)", fontSize: "0.78rem" }}>{r.email}</div>
                                </td>
                                <td>{r.age}</td>
                                <td>{r.coverage_tier != null ? `$${Number(r.coverage_tier).toLocaleString()}` : "—"}</td>
                                <td>
                                    <span className={`admin-enroll-status ${r.status}`}>
                                        {r.status === "submitted" ? "Enrolled"
                                            : r.status === "in_progress" ? "In progress"
                                            : r.status === "declined_health" ? "Declined"
                                            : r.status}
                                    </span>
                                </td>
                                <td>
                                    {r.submitted_at
                                        ? new Date(r.submitted_at).toLocaleDateString()
                                        : (r.last_saved_at ? `saved ${new Date(r.last_saved_at).toLocaleDateString()}` : "—")}
                                </td>
                                <td>
                                    {r.status === "submitted" && (
                                        <a className="admin-btn secondary" href={adminEmployeeApplicationPdfUrl(r.application_id)}
                                           target="_blank" rel="noopener noreferrer"
                                           data-testid={`ee-pdf-${r.application_id}`}>
                                            PDF
                                        </a>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

export default function AdminSubmissionDetail() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [me, setMe] = useState(null);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [transitioning, setTransitioning] = useState(false);
    const [err, setErr] = useState(null);
    const [returnNotes, setReturnNotes] = useState("");
    const [showReturnForm, setShowReturnForm] = useState(false);

    const load = useCallback(async () => {
        try {
            const d = await adminSubmissionDetail(id);
            setDetail(d);
        } catch (e) {
            if (e?.response?.status === 401) {
                navigate("/admin/login");
                return;
            }
            if (e?.response?.status === 404) {
                setErr("Application not found.");
            } else {
                setErr("Couldn't load this submission.");
            }
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        document.title = "Laguna · submission";
        track("admin_submission_detail_view", { applicationId: id });
        (async () => {
            try {
                const m = await adminMe();
                setMe(m);
                await load();
            } catch (_) {
                navigate("/admin/login");
            }
        })();
    }, [id, load, navigate]);

    const onSignOut = async () => {
        await adminLogout();
        navigate("/admin/login");
    };

    const transition = async (to_status, admin_notes) => {
        setTransitioning(true);
        setErr(null);
        try {
            await adminTransitionStatus(id, to_status, admin_notes);
            track("admin_status_transition", { applicationId: id, toStatus: to_status });
            setShowReturnForm(false);
            setReturnNotes("");
            await load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "object" ? d?.message || "Could not transition." : (d || "Could not transition."));
        } finally {
            setTransitioning(false);
        }
    };

    if (loading) {
        return (
            <div className="admin-page" data-testid="admin-detail-loading">
                <nav className="admin-nav">
                    <div className="logo-mark" onClick={() => navigate("/")}>Laguna<span>.</span></div>
                </nav>
                <div className="admin-wrap">
                    <p className="muted">Loading…</p>
                </div>
            </div>
        );
    }
    if (err && !detail) {
        return (
            <div className="admin-page">
                <nav className="admin-nav">
                    <div className="logo-mark" onClick={() => navigate("/")}>Laguna<span>.</span></div>
                </nav>
                <div className="admin-wrap">
                    <div className="review-err" data-testid="admin-detail-error">{err}</div>
                    <button
                        className="admin-btn secondary"
                        onClick={() => navigate("/admin/submissions")}
                    >←︎ Back to submissions</button>
                </div>
            </div>
        );
    }

    const app = detail.application || {};
    const proposal = detail.proposal || null;
    const cci = app.confirmed_company_info || {};
    const hr = app.hr_admin_contact || {};
    const comm = app.communication_preferences || {};
    const pc = (proposal?.company) || {};
    const pq = (proposal?.preliminary_quote) || {};
    const status = app.status || "in_progress";
    const actions = [];
    if (status === "submitted_to_laguna") {
        actions.push({ label: "Mark as submitted to MassMutual", to: "submitted_to_massmutual", variant: "primary" });
        actions.push({ label: "Return to employer for revisions", to: "returned_to_employer", variant: "secondary", requiresNotes: true });
    } else if (status === "submitted_to_massmutual") {
        actions.push({ label: "Mark policy as issued", to: "policy_issued", variant: "primary" });
        actions.push({ label: "Mark as rejected", to: "rejected", variant: "danger" });
    }

    return (
        <div className="admin-page admin-detail" data-testid="admin-detail">
            <nav className="admin-nav">
                <div style={{ display: "flex", alignItems: "center", gap: "2rem", flexWrap: "wrap" }}>
                    <div className="logo-mark" onClick={() => navigate("/")}>Laguna<span>.</span></div>
                    <div className="admin-bread">
                        Admin <span className="current">{cci.legal_name || pc.legal_name || "—"}</span>
                    </div>
                </div>
                <div className="admin-session">
                    <span>{me?.email}</span>
                    <button className="signout" onClick={onSignOut} data-testid="admin-signout">Sign out</button>
                </div>
            </nav>

            <div className="admin-wrap">
                <button
                    className="admin-btn secondary"
                    onClick={() => navigate("/admin/submissions")}
                    style={{ marginBottom: "1.5rem" }}
                    data-testid="admin-back-link"
                >←︎ All submissions</button>

                <div className="section-kicker">Application</div>
                <h1 className="admin-h1" data-testid="admin-detail-company">
                    {cci.legal_name || pc.legal_name || "Client"}
                </h1>
                <p className="admin-sub">
                    <span className={`status-pill ${status}`} data-testid="admin-detail-status">
                        {status.replace(/_/g, " ")}
                    </span>
                    {" · "}Application ID: <code>{app.application_id}</code>
                </p>

                {err && <div className="review-err">{err}</div>}

                {/* Action bar */}
                <div className="admin-actions">
                    <a
                        className="admin-btn"
                        href={adminSubmissionPdfUrl(app.application_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="admin-download-pdf"
                    >Download filled PDF</a>
                    <a
                        className="admin-btn"
                        href={adminSubmissionCensusXlsxUrl(app.application_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="admin-download-census"
                    >Download MassMutual census (XLSX)</a>
                    {actions.map((a) => (
                        <button
                            key={a.to}
                            type="button"
                            className={`admin-btn ${a.variant}`}
                            disabled={transitioning}
                            onClick={() => {
                                if (a.requiresNotes) {
                                    setShowReturnForm(true);
                                } else {
                                    transition(a.to);
                                }
                            }}
                            data-testid={`admin-transition-${a.to}`}
                        >
                            {a.label}
                        </button>
                    ))}
                </div>

                {showReturnForm && (
                    <div className="attestation-wrap" style={{ flexDirection: "column", alignItems: "stretch", gap: "0.5rem" }}>
                        <label htmlFor="return-notes" className="mono" style={{ fontSize: "0.7rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)" }}>
                            Return-to-employer notes (required)
                        </label>
                        <textarea
                            id="return-notes"
                            rows={4}
                            value={returnNotes}
                            onChange={(e) => setReturnNotes(e.target.value)}
                            style={{ padding: "0.85rem 1rem", border: "1px solid var(--line)" }}
                            placeholder="What does the employer need to revise?"
                            data-testid="admin-return-notes"
                        />
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button
                                className="admin-btn primary"
                                onClick={() => transition("returned_to_employer", returnNotes)}
                                disabled={!returnNotes.trim() || transitioning}
                                data-testid="admin-return-confirm"
                            >Send back to employer</button>
                            <button
                                className="admin-btn secondary"
                                onClick={() => setShowReturnForm(false)}
                            >Cancel</button>
                        </div>
                    </div>
                )}

                {(status === "policy_issued" || status === "enrollment_open") && (
                    <CensusUploadPanel
                        applicationId={app.application_id}
                        status={status}
                        onDone={load}
                    />
                )}

                {(status === "policy_issued" || status === "enrollment_open") && (
                    <EmployeeEnrollmentsBlock applicationId={app.application_id} />
                )}

                {/* Phase 3a §5 — Underwriting outcomes panel. Always visible when
                    the application has an employer_id so admins can record the
                    MM decision even before status flips to policy_issued. */}
                {app.employer_id && (
                    <UnderwritingOutcomesPanel employerId={app.employer_id} />
                )}

                {/* Company */}
                <div className="detail-section">
                    <h3>Company</h3>
                    <dl className="kv">
                        <dt>Legal name</dt><dd>{cci.legal_name || pc.legal_name || "—"}</dd>
                        <dt>DBA</dt><dd>{cci.dba_name || "—"}</dd>
                        <dt>Headcount</dt><dd>{cci.headcount ?? pc.employee_count ?? "—"}</dd>
                        <dt>Situs state</dt><dd>{pc.parsed_state_name || cci.state || "—"}</dd>
                        <dt>EIN</dt><dd>{cci.ein_set ? (cci.ein_mask || "••-•••••••") : "(not provided)"}</dd>
                        <dt>Address</dt><dd>{[cci.address1, cci.address2, cci.city, cci.state, cci.postal_code].filter(Boolean).join(", ") || pc.address || "—"}</dd>
                    </dl>
                </div>

                {/* Payroll */}
                <div className="detail-section">
                    <h3>Payroll + effective date</h3>
                    <dl className="kv">
                        <dt>Payroll frequency</dt><dd>{cci.payroll_frequency || pc.payroll_frequency || "—"}</dd>
                        <dt>Deduction frequency</dt><dd>{app.deduction_frequency || "—"}</dd>
                        <dt>Requested effective date</dt><dd>{app.effective_date_requested || "—"}</dd>
                    </dl>
                </div>

                {/* Plan */}
                <div className="detail-section">
                    <h3>Plan design</h3>
                    <dl className="kv">
                        <dt>Location model</dt><dd>{app.location_model === "single" ? "Single location" : `${(app.locations || []).length} locations`}</dd>
                        <dt>Class structure</dt><dd>{app.class_structure === "single_class" ? "Single class" : "Multiple classes"}</dd>
                        <dt>Eligibility window</dt><dd>{app.eligibility_window || "—"}</dd>
                        <dt>Waiting period</dt><dd>{app.waiting_period_days != null ? `${app.waiting_period_days} days` : "—"}</dd>
                        <dt>Riders</dt><dd>{riderSummary(app.rider_elections)}</dd>
                        <dt>ERISA status</dt><dd>{app.erisa_status || "—"}</dd>
                        <dt>ERISA notes</dt><dd>{app.erisa_notes || "—"}</dd>
                    </dl>
                </div>

                {/* HR + comms */}
                <div className="detail-section">
                    <h3>HR contact + communication</h3>
                    <dl className="kv">
                        <dt>HR admin name</dt><dd>{hr.name || "—"}</dd>
                        <dt>HR admin email</dt><dd>{hr.email || "—"}</dd>
                        <dt>HR admin phone</dt><dd>{hr.work_phone || hr.cell_phone || "—"}</dd>
                        <dt>Email comms</dt><dd>{comm.email ? "Yes" : "No"}</dd>
                        <dt>Physical letters</dt><dd>{comm.physical_letters ? "Yes" : "No"}</dd>
                        <dt>Voice drops</dt><dd>{comm.voice_drops ? "Yes" : "No"}</dd>
                    </dl>
                </div>

                {/* Proposal snapshot */}
                {proposal && (
                    <div className="detail-section">
                        <h3>Preliminary quote snapshot</h3>
                        <dl className="kv">
                            <dt>Per-employee low</dt><dd>${pq.lower_per_period ?? "—"}</dd>
                            <dt>Per-employee high</dt><dd>${pq.upper_per_period ?? "—"}</dd>
                            <dt>Projected annual</dt><dd>${pq.projected_annual_company_premium ?? "—"}</dd>
                            <dt>Rate assumptions</dt><dd>{proposal.rate_assumptions_version || "—"}</dd>
                            <dt>View count</dt><dd>{proposal.view_count ?? 0}</dd>
                            <dt>Last viewed</dt><dd>{fmt(proposal.last_viewed_at)}</dd>
                        </dl>
                    </div>
                )}

                {/* Attestation */}
                {app.submitted_at && (
                    <div className="detail-section">
                        <h3>Employer attestation</h3>
                        <dl className="kv">
                            <dt>Attested by</dt><dd>{app.attested_by || "—"}</dd>
                            <dt>Attested at</dt><dd>{fmt(app.submitted_at)}</dd>
                            <dt>Attestation IP</dt><dd>{app.attestation_ip || "—"}</dd>
                            <dt>Attestation UA</dt><dd style={{ fontSize: "0.78rem", color: "var(--muted)", wordBreak: "break-all" }}>{app.attestation_ua || "—"}</dd>
                        </dl>
                    </div>
                )}

                {/* Admin notes */}
                {app.admin_notes && (
                    <div className="detail-section">
                        <h3>Admin notes</h3>
                        <p>{app.admin_notes}</p>
                    </div>
                )}

                {/* Activity timeline */}
                <div className="timeline-block" data-testid="admin-timeline">
                    <h3 style={{ marginBottom: "1rem", fontFamily: "Fraunces, serif", fontWeight: 700, fontSize: "1.05rem" }}>Activity timeline</h3>
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                        {(app.activity_log || []).slice(-12).reverse().map((e, i) => (
                            <li key={i}>
                                <div className="ts">{fmt(e.at)}</div>
                                <div>
                                    <strong>{e.action}</strong>
                                    {e.step ? ` · step ${e.step}` : ""}
                                    {e.email ? ` · ${e.email}` : ""}
                                    {e.notes ? <span style={{ display: "block", color: "var(--muted)", marginTop: 4 }}>{e.notes}</span> : null}
                                </div>
                            </li>
                        ))}
                        {(!app.activity_log || app.activity_log.length === 0) && (
                            <li style={{ fontStyle: "italic", color: "var(--muted)" }}>No activity yet.</li>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
}
